import { Checkbox } from '@/global/components/Checkbox'
import { Combobox } from '@/global/components/Combobox'
import { useAuth } from '@/hooks'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import { filterLocaisCentro } from '@acme/common'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario, Funcionario } from 'service/api'
import { useStore } from 'store'
import { HELPER_TEXT_FORM, SelectProps } from './types'

const DEBOUNCE_MS = 300

type Props = Omit<
    SelectProps,
    'buttom' | 'isSupervisor' | 'form' | 'defaultValue' | 'variant'
> & {
    children?: never
    /**
     * `form` = preset para formulários: largura 100%, size `medium` e
     * helperText reservado para manter o alinhamento com os demais campos.
     */
    variant?: SelectProps['variant'] | 'form'
    checked?: number[]
    onChange?: (ids: number[]) => void
    defaultValues?: number[]
    /** Utiliza filtro de locais e centro de custo */
    useFilter?: boolean
    value?: number[]
    form?: boolean | string
    /** Chamado quando o estado de "Todos" muda */
    onSelectAllChange?: (isAll: boolean) => void
    /** Exibe checkbox "Mostrar demitidos" que inclui rescindidos na lista */
    permitirDemitidos?: boolean
    /** Lista também funcionários rescindidos (necessário em relatórios de período) */
    incluirRescindidos?: boolean
}

export const SelectFuncionarios: React.FC<Props> = ({
    size,
    form,
    disabled,
    checked,
    onChange,
    defaultValues = [],
    useFilter = false,
    value,
    width,
    variant: _variant,
    helperText,
    onSelectAllChange,
    permitirDemitidos = false,
    incluirRescindidos = false,
    className,
}) => {
    const variant = _variant ?? (form ? 'form' : 'filled')
    const isForm = variant === 'form'
    const selectSize = size ?? (isForm ? 'medium' : 'small')
    const selectWidth = width ?? (isForm ? '100%' : 260)
    const selectHelperText =
        helperText ?? (isForm ? HELPER_TEXT_FORM : undefined)

    const formName = typeof form === 'string' ? form : 'funcionarioIds'
    const {
        decoded: { categoria },
    } = useAuth()
    const formContext = useFormContext()
    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const locais = useStore(state => state.locaisIds)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])
    const diasRescisao = useStore(state => state.company?.diasRescisao)

    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [selectAll, setSelectAll] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const handleSearchChange = useCallback((valor: string) => {
        setSearch(valor)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(valor)
        }, DEBOUNCE_MS)
    }, [])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const queryParams: Record<string, unknown> = { empresaId }
    if (useFilter && centroCustoId > 0)
        queryParams.centroCustoId = centroCustoId
    if (useFilter && locais.length) queryParams.localId = locais[0]
    if (debouncedSearch) queryParams.search = debouncedSearch

    const {
        data: pages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
    } = funcionario.useInfiniteScroll({
        queryParams,
        enabled: empresaId > 0,
    })

    const allLoaded = useMemo(
        () => pages?.pages.flatMap(p => p.data) ?? [],
        [pages],
    ) as Funcionario[]

    const exibirApenasAtivos =
        !incluirRescindidos && categoria !== CategoriaEnum.Funcionario
    const [mostrarDemitidos, setMostrarDemitidos] = useState(false)

    const isDemitido = useCallback(
        (obj: Funcionario) => {
            if (!obj.dtRescisao) return false
            const dataRef =
                    competencia === competenciaAtual
                        ? dayjs()
                        : dayjs(competencia).startOf('month'),
                tolerancia = diasRescisao ?? 40

            return dataRef
                .add(tolerancia, 'day')
                .isAfter(dayjs(obj.dtRescisao), 'day')
        },
        [competencia, competenciaAtual, diasRescisao],
    )

    const filtrarFuncionario = useCallback(
        (obj: Funcionario) => {
            if (exibirApenasAtivos && !mostrarDemitidos && isDemitido(obj))
                return false

            if (useFilter === false) return true
            return filterLocaisCentro<typeof obj>(
                locais,
                [centroCustoId!],
                it => it as any,
            )(obj)
        },
        [
            exibirApenasAtivos,
            mostrarDemitidos,
            isDemitido,
            centroCustoId,
            locais,
            useFilter,
        ],
    )
    const data = useMemo(
        () => allLoaded.filter(filtrarFuncionario),
        [allLoaded, filtrarFuncionario],
    )

    const [_employeeIds, _setEmployeeIds] = useState<number[]>(defaultValues)
    const employeeIds = value ? value : _employeeIds
    const setEmployeeIds = (ids: number[]) => {
        _setEmployeeIds(ids)
        onChange?.(ids)
    }

    useEffect(() => {
        if (useFilter && (centroCustoId > 0 || locais.length)) {
            setEmployeeIds([])
            setSelectAll(false)
            onSelectAllChange?.(false)
        }
    }, [centroCustoId, locais, useFilter])

    const { data: todosFuncionarios = [] } = funcionario.useFetch<
        Funcionario[]
    >({
        queryParams: {
            empresaId,
            ...(useFilter && centroCustoId > 0 ? { centroCustoId } : {}),
            ...(useFilter && locais.length ? { localId: locais[0] } : {}),
        } as any,
        enabled: selectAll && empresaId > 0,
    })

    useEffect(() => {
        if (!selectAll) return
        const ids = todosFuncionarios
            .filter(filtrarFuncionario)
            .map(({ id }) => id)
            .filter((id): id is number => id != null && !checked?.includes(id))
        setEmployeeIds(ids)
    }, [selectAll, todosFuncionarios, filtrarFuncionario])

    // Ao desmarcar "Mostrar demitidos", remove da seleção apenas os
    // demitidos conhecidos, evitando seleção fantasma no form. Com "Todos"
    // marcado a recomputação do effect acima já cobre o caso.
    const prevMostrarDemitidos = useRef(false)
    useEffect(() => {
        if (prevMostrarDemitidos.current && !mostrarDemitidos && !selectAll) {
            const demitidos = new Set(
                allLoaded.filter(isDemitido).map(({ id }) => id),
            )
            if (employeeIds.some(id => demitidos.has(id))) {
                setEmployeeIds(employeeIds.filter(id => !demitidos.has(id)))
            }
        }
        prevMostrarDemitidos.current = mostrarDemitidos
    }, [mostrarDemitidos])

    useEffect(() => {
        if (form && formContext) {
            formContext.setValue(formName, employeeIds)
        }
    }, [employeeIds])

    const travados = checked ?? []

    // Os já associados entram como selecionados só para aparecerem marcados —
    // são opções desabilitadas, então nunca saem da lista pelo clique.
    const idsSelecionados = useMemo(
        () => [
            ...travados,
            ...employeeIds.filter(id => !travados.includes(id)),
        ],
        [checked, employeeIds],
    )

    const opcoes = useMemo(() => data.map(({ id }) => id), [data])

    const nomePorId = useMemo(() => {
        const mapa = new Map<number, Funcionario>()
        for (const obj of allLoaded) mapa.set(obj.id, obj)
        return mapa
    }, [allLoaded])

    const rotuloDe = useCallback(
        (id: number) => {
            const obj = nomePorId.get(id)
            if (!obj) return ''
            return exibirApenasAtivos && isDemitido(obj)
                ? `${obj.nome} (Demitido)`
                : obj.nome
        },
        [nomePorId, exibirApenasAtivos, isDemitido],
    )

    const handleChange = (ids: number[]) => {
        setSelectAll(false)
        onSelectAllChange?.(false)
        setEmployeeIds(ids.filter(id => !travados.includes(id)))
    }

    const alternarTodos = () => {
        const proximo = !selectAll
        setSelectAll(proximo)
        setEmployeeIds([])
        onSelectAllChange?.(proximo)
    }

    const carregarMais = useCallback(() => {
        if (!isFetchingNextPage) fetchNextPage()
    }, [fetchNextPage, isFetchingNextPage])

    const renderValue = (ids: number[]) => {
        if (selectAll) return 'Todos'

        const visiveis = ids.filter(id => !travados.includes(id))
        const nomes = visiveis
            .map(id => nomePorId.get(id)?.nome)
            .filter((nome): nome is string => !!nome)

        const naoCarregados = visiveis.length - nomes.length

        return (
            naoCarregados > 0 ? [...nomes, `+${naoCarregados}`] : nomes
        ).join(', ')
    }

    const erro = form ? formContext?.formState.errors[formName] : undefined
    const mensagemErro =
        typeof erro?.message === 'string' ? erro.message : undefined

    const exibirCheckboxDemitidos = permitirDemitidos && exibirApenasAtivos

    return (
        <div
            className={cn(
                'flex min-w-0 max-w-full items-center gap-2',
                className,
            )}
            style={{ width: selectWidth }}
        >
            <Combobox<number>
                className="min-w-0 flex-1"
                id={form ? `${formName}-select` : undefined}
                label="Funcionários"
                multiple
                size={selectSize}
                variant={variant === 'outlined' ? 'outlined' : 'filled'}
                placeholder="Selecione os funcionários"
                options={opcoes}
                value={idsSelecionados}
                onChange={handleChange}
                getOptionLabel={rotuloDe}
                getOptionKey={id => String(id)}
                getOptionDisabled={id => travados.includes(id)}
                isOptionEqualToValue={(option, valor) => option === valor}
                renderValue={renderValue}
                // busca e paginação são do servidor
                inputValue={search}
                onInputChange={handleSearchChange}
                loading={isFetching}
                hasMore={hasNextPage}
                onLoadMore={carregarMais}
                loadingMore={isFetchingNextPage}
                disabled={disabled}
                noOptionsText="Nenhum funcionário encontrado"
                error={!!erro}
                helperText={mensagemErro ?? selectHelperText}
                footer={
                    <div className="flex flex-col gap-1">
                        <Checkbox
                            label="Todos"
                            checked={selectAll}
                            onCheckedChange={alternarTodos}
                        />
                        {exibirCheckboxDemitidos && (
                            <Checkbox
                                label="Mostrar demitidos"
                                checked={mostrarDemitidos}
                                onCheckedChange={marcado =>
                                    setMostrarDemitidos(marcado === true)
                                }
                            />
                        )}
                    </div>
                }
            />
        </div>
    )
}
